import { useMemo, useState } from 'react'
import { Modal, Button, Input, Select } from '../ui'
import { templatesApiService } from '../../services/templatesApiService'
import { ApiError } from '../../services/BaseApiService'
import type { Folder } from '../../types'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { ancestorChain } from '../../utils/folderTree'

const ROOT = '__root__'

interface CopyVersionModalProps {
  version: { templateVersionId: string; versionNumber: number }
  templateName: string
  folders: Folder[]
  /** Where the source template lives; the copy defaults to the same folder. */
  defaultFolderId: string | null
  onClose: () => void
  onCopied: (newTemplateId: string) => void
}

/** Creates a brand-new template whose v1 is a copy of the given version. The copy is not live. */
export default function CopyVersionModal({
  version, templateName, folders, defaultFolderId, onClose, onCopied,
}: CopyVersionModalProps) {
  const [name, setName] = useState(`${templateName} (copy)`)
  const [folderId, setFolderId] = useState<string>(defaultFolderId ?? ROOT)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const folderOptions = useMemo(() => [
    { value: ROOT, label: 'Top level' },
    ...folders
      .map(f => ({ value: f.folderId, label: ancestorChain(folders, f.folderId).map(a => a.name).join(' / ') }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ], [folders])

  async function handleCopy() {
    if (!name.trim()) return
    setError(''); setLoading(true)
    try {
      const result = await templatesApiService.copyVersion(version.templateVersionId, {
        name: name.trim(),
        folderId: folderId === ROOT ? null : folderId,
      })
      onCopied(result.templateId)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Copy failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Copy as new template"
      subtitle={`Starts a new template from v${version.versionNumber}. The copy is not in Live.`}
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handleCopy} loading={loading} disabled={!name.trim()}>
            Create copy
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        {error && (
          <div role="alert" style={{
            background: colors.errorLight, color: colors.error,
            border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
            padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm,
          }}>{error}</div>
        )}
        <Input
          label="Name"
          aria-label="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          autoFocus
        />
        <Select
          label="Folder"
          value={folderId}
          onChange={v => setFolderId(String(v))}
          options={folderOptions}
          isSearchable
        />
      </div>
    </Modal>
  )
}
